import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import ThumbDownAltIcon from '@mui/icons-material/ThumbDownAlt';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import { Alert, Box, Button, Chip, Fab, Fade, IconButton, Paper, TextField, Typography } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';

const TAB_TAGS = {
    documents: {
        up: ['#well-organized', '#easy-to-navigate', '#complete'],
        down: ['#missing-doc', '#wrong-status', '#upload-failed'],
    },
    incident_details: {
        up: ['#accurate', '#complete', '#helpful'],
        down: ['#wrong-date', '#wrong-parties', '#missing-info', '#incomplete'],
    },
    medical_chronology: {
        up: ['#accurate', '#complete', '#fast', '#well-organized'],
        down: ['#missing-entries', '#wrong-dates', '#wrong-provider', '#incomplete', '#duplicate'],
    },
    medical_expenses: {
        up: ['#accurate', '#totals-correct', '#complete'],
        down: ['#wrong-amounts', '#missing-bill', '#wrong-provider', '#duplicate'],
    },
    timeline: {
        up: ['#accurate', '#complete', '#helpful'],
        down: ['#missing-event', '#wrong-date', '#incorrect-detail'],
    },
    discovery: {
        up: ['#accurate', '#useful', '#complete'],
        down: ['#missing-item', '#wrong-answer', '#not-useful'],
    },
};

const CROSS_TAGS = ['#mostly-accurate', '#some-errors'];

const TAB_LABELS = {
    documents: 'Documents',
    incident_details: 'Incident Details',
    medical_chronology: 'Medical Chronology',
    medical_expenses: 'Medical Expenses',
    timeline: 'Timeline',
    discovery: 'Discovery',
};

const SHOW_DELAY_MS = 2500;
const AUTO_DISMISS_MS = 60000;

function escapeRegExp(str) {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function isTagSelected(text, tag) {
    return new RegExp(`(?:^|\\s)${escapeRegExp(tag)}(?:\\s|$)`).test(text);
}

/**
 * Floating thumbs-up / thumbs-down feedback widget.
 * Appears after processing completes (with a short delay) and auto-dismisses
 * after AUTO_DISMISS_MS of inactivity. Dismissed tabs are tracked via the
 * dismissedTabsRef from Case.js so state survives tab switches.
 */
export default function TabFeedback({ caseId, tabSlug, processingComplete, dismissedTabsRef }) {
    const [fabsVisible, setFabsVisible] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const [sentiment, setSentiment] = useState(null); // 'up' | 'down'
    const [message, setMessage] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [submitError, setSubmitError] = useState(null);

    const showTimerRef = useRef(null);
    const dismissTimerRef = useRef(null);
    const hasSubmittedRef = useRef(false);

    const dismiss = useCallback(() => {
        dismissedTabsRef.current.add(tabSlug);
        setFabsVisible(false);
        setExpanded(false);
        setSentiment(null);
        setMessage('');
        setSubmitted(false);
        setSubmitError(null);
        hasSubmittedRef.current = false;
    }, [tabSlug, dismissedTabsRef]);

    const resetDismissTimer = useCallback(() => {
        if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
        dismissTimerRef.current = setTimeout(dismiss, AUTO_DISMISS_MS);
    }, [dismiss]);

    // Reset and schedule appearance whenever the active tab or processing state changes.
    useEffect(() => {
        if (showTimerRef.current) clearTimeout(showTimerRef.current);
        if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);

        setFabsVisible(false);
        setExpanded(false);
        setSentiment(null);
        setMessage('');
        setSubmitted(false);
        setSubmitError(null);

        if (!processingComplete || dismissedTabsRef.current.has(tabSlug)) {
            return;
        }

        showTimerRef.current = setTimeout(() => {
            if (!dismissedTabsRef.current.has(tabSlug)) {
                setFabsVisible(true);
                resetDismissTimer();
            }
        }, SHOW_DELAY_MS);

        return () => {
            if (showTimerRef.current) clearTimeout(showTimerRef.current);
            if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [processingComplete, tabSlug]);

    function handleThumbClick(s) {
        setSentiment(s);
        setExpanded(true);
        setMessage('');
        setSubmitError(null);
        setSubmitted(false);
        resetDismissTimer();
    }

    function handleTagClick(tag) {
        setMessage(prev => {
            if (isTagSelected(prev, tag)) {
                return prev.replace(new RegExp(`\\s*${escapeRegExp(tag)}\\s*`), ' ').replace(/^\s+|\s+$/g, '') + ' ';
            }
            const sep = prev.endsWith(' ') || prev === '' ? '' : ' ';
            return prev + sep + tag + ' ';
        });
        resetDismissTimer();
    }

    async function submitFeedback({ showThankYou = false } = {}) {
        if (!sentiment || hasSubmittedRef.current) {
            return true;
        }

        setSubmitting(true);
        setSubmitError(null);
        try {
            const response = await fetch('/api/tab-feedback', {
                method: 'POST',
                credentials: 'same-origin',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    sentiment: sentiment === 'up' ? 'thumbs_up' : 'thumbs_down',
                    tab: tabSlug,
                    message: message.trim(),
                    caseId,
                    url: window.location.pathname,
                }),
            });
            if (!response.ok) throw new Error('Server error');
            hasSubmittedRef.current = true;
            if (showThankYou) {
                setSubmitted(true);
                setTimeout(dismiss, 2000);
            }
            return true;
        } catch {
            setSubmitError('Something went wrong. Please try again.');
            return false;
        } finally {
            setSubmitting(false);
        }
    }

    async function handleSubmit() {
        if (submitting) return;
        await submitFeedback({ showThankYou: true });
    }

    async function handleCloseExpanded() {
        if (submitting) return;
        const ok = await submitFeedback();
        if (ok) {
            dismiss();
        }
    }

    const tags = TAB_TAGS[tabSlug] || { up: ['#helpful', '#accurate'], down: ['#inaccurate', '#incomplete'] };
    const directedTags = sentiment ? tags[sentiment] : [];
    const allTags = [...directedTags, ...CROSS_TAGS];
    const tabLabel = TAB_LABELS[tabSlug] || tabSlug;
    const heading = sentiment === 'up'
        ? `What went well with ${tabLabel}?`
        : `What could be better with ${tabLabel}?`;

    const fabSx = {
        background: 'var(--background-color)',
        border: '1.5px solid var(--border-color)',
        boxShadow: 'var(--shadow-medium)',
        color: 'var(--text-secondary)',
        transition: 'all 0.2s ease',
    };

    return (
        <>
            {/* Floating thumb buttons — fixed center-bottom, shown before expansion */}
            <Fade in={fabsVisible && !expanded} timeout={{ enter: 500, exit: 200 }} unmountOnExit>
                <Box
                    sx={{
                        position: 'fixed',
                        bottom: 28,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 1300,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 1,
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            background: 'var(--background-color)',
                            border: '1px solid var(--border-color)',
                            borderRadius: 6,
                            px: 1.5,
                            py: 0.4,
                            boxShadow: 'var(--shadow-subtle)',
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: '0.72rem',
                                color: 'var(--text-muted)',
                                fontWeight: 500,
                                letterSpacing: '0.04em',
                                textTransform: 'uppercase',
                                userSelect: 'none',
                            }}
                        >
                            Rate this result
                        </Typography>
                        <IconButton
                            size="small"
                            onClick={dismiss}
                            aria-label="Dismiss feedback prompt"
                            sx={{
                                p: 0.25,
                                color: 'var(--text-muted)',
                                '&:hover': { color: 'var(--text-secondary)', background: 'transparent' },
                            }}
                        >
                            <CloseIcon sx={{ fontSize: '0.85rem' }} />
                        </IconButton>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <Fab
                            size="medium"
                            onClick={() => handleThumbClick('up')}
                            aria-label="Rate positively"
                            sx={{
                                ...fabSx,
                                '&:hover': {
                                    background: '#f0fdf4',
                                    borderColor: 'var(--success-color)',
                                    color: 'var(--success-color)',
                                    transform: 'translateY(-3px)',
                                    boxShadow: '0 6px 20px rgba(16, 185, 129, 0.25)',
                                },
                            }}
                        >
                            <ThumbUpAltIcon />
                        </Fab>
                        <Fab
                            size="medium"
                            onClick={() => handleThumbClick('down')}
                            aria-label="Rate negatively"
                            sx={{
                                ...fabSx,
                                '&:hover': {
                                    background: '#fef2f2',
                                    borderColor: 'var(--error-color)',
                                    color: 'var(--error-color)',
                                    transform: 'translateY(-3px)',
                                    boxShadow: '0 6px 20px rgba(239, 68, 68, 0.25)',
                                },
                            }}
                        >
                            <ThumbDownAltIcon />
                        </Fab>
                    </Box>
                </Box>
            </Fade>

            {/* Expanded feedback card */}
            <Fade in={expanded} timeout={300} unmountOnExit>
                <Paper
                    elevation={0}
                    sx={{
                        position: 'fixed',
                        bottom: 28,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 1300,
                        width: 360,
                        maxWidth: 'calc(100vw - 32px)',
                        borderRadius: '16px',
                        border: '1px solid var(--border-color)',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.13), 0 2px 8px rgba(0,0,0,0.06)',
                        background: 'var(--background-color)',
                        p: 2.5,
                    }}
                >
                    {submitted ? (
                        <Box sx={{ textAlign: 'center', py: 0.5 }}>
                            <Typography sx={{ fontSize: '1.6rem', mb: 0.5 }}>
                                {sentiment === 'up' ? '🙌' : '🙏'}
                            </Typography>
                            <Typography sx={{ color: 'var(--text-primary)', fontWeight: 500, fontSize: '0.95rem' }}>
                                Thanks for your feedback!
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            {/* Card header */}
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                    {sentiment === 'up'
                                        ? <ThumbUpAltIcon sx={{ fontSize: '1rem', color: 'var(--success-color)', flexShrink: 0 }} />
                                        : <ThumbDownAltIcon sx={{ fontSize: '1rem', color: 'var(--error-color)', flexShrink: 0 }} />
                                    }
                                    <Typography sx={{ fontSize: '0.88rem', fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1.35 }}>
                                        {heading}
                                    </Typography>
                                </Box>
                                <IconButton
                                    size="small"
                                    onClick={handleCloseExpanded}
                                    disabled={submitting}
                                    aria-label="Close feedback form"
                                    sx={{ ml: 1, flexShrink: 0, color: 'var(--text-muted)', '&:hover': { color: 'var(--text-secondary)' } }}
                                >
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </Box>

                            {/* Tag chips */}
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}>
                                {allTags.map(tag => {
                                    const selected = isTagSelected(message, tag);
                                    return (
                                        <Chip
                                            key={tag}
                                            label={tag}
                                            size="small"
                                            onClick={() => handleTagClick(tag)}
                                            sx={{
                                                fontSize: '0.78rem',
                                                height: 26,
                                                cursor: 'pointer',
                                                fontWeight: selected ? 600 : 400,
                                                color: selected ? 'var(--text-primary)' : 'var(--text-secondary)',
                                                background: selected ? 'var(--selected-bg)' : 'var(--surface-color)',
                                                border: selected ? '1px solid var(--accent-color)' : '1px solid var(--border-color)',
                                                transition: 'all 0.15s ease',
                                                '&:hover': {
                                                    background: 'var(--surface-hover)',
                                                    borderColor: 'var(--accent-color)',
                                                },
                                            }}
                                        />
                                    );
                                })}
                            </Box>

                            {/* Free-text input */}
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                placeholder="Add a comment (optional)..."
                                value={message}
                                onChange={e => { setMessage(e.target.value); resetDismissTimer(); }}
                                size="small"
                                sx={{
                                    mb: 1.5,
                                    '& .MuiOutlinedInput-root': {
                                        fontSize: '0.88rem',
                                        color: 'var(--text-primary)',
                                        '&:hover fieldset': { borderColor: 'var(--accent-color)' },
                                        '&.Mui-focused fieldset': { borderColor: 'var(--accent-color)' },
                                    },
                                }}
                            />

                            {submitError && (
                                <Alert severity="error" sx={{ mb: 1.5, py: 0.25, fontSize: '0.82rem' }}>
                                    {submitError}
                                </Alert>
                            )}

                            {/* Action buttons */}
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                    size="small"
                                    variant="contained"
                                    onClick={handleSubmit}
                                    disabled={submitting}
                                    startIcon={submitting ? null : <SendIcon sx={{ fontSize: '0.9rem !important' }} />}
                                    sx={{
                                        background: 'var(--button-gradient)',
                                        boxShadow: '0 4px 12px rgba(212, 175, 55, 0.35)',
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        fontSize: '0.85rem',
                                        borderRadius: '8px',
                                        px: 2,
                                        '&:hover': {
                                            background: 'var(--button-gradient-hover)',
                                            boxShadow: '0 6px 18px rgba(212, 175, 55, 0.5)',
                                        },
                                        '&:disabled': {
                                            background: 'var(--text-disabled)',
                                            boxShadow: 'none',
                                            color: 'white',
                                        },
                                    }}
                                >
                                    {submitting ? 'Sending...' : 'Send'}
                                </Button>
                            </Box>
                        </>
                    )}
                </Paper>
            </Fade>
        </>
    );
}
